import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Layout } from "./components/Layout";
import { LoginPage } from "./pages/LoginPage";
import { SitesPage } from "./pages/SitesPage";
import { SiteDetailPage } from "./pages/SiteDetailPage";
import { LeadsPage } from "./pages/LeadsPage";
import { ClientsPage } from "./pages/ClientsPage";
import { JournoPage } from "./pages/JournoPage";
import { OutreachPage } from "./pages/OutreachPage";
import { OpsPage } from "./pages/OpsPage";
import { CommandCenterPage } from "./pages/CommandCenterPage";
import { InboxPage } from "./pages/InboxPage";
import { TestimonialsPage } from "./pages/TestimonialsPage";
import { CitationsPage } from "./pages/CitationsPage";
import { AgentsPage } from "./pages/AgentsPage";
import { AgentDetailPage } from "./pages/AgentDetailPage";
import { AgentsPendingPage } from "./pages/AgentsPendingPage";
import { OptimizePage } from "./pages/OptimizePage";
import { OptimizeImpactPage } from "./pages/OptimizeImpactPage";
import { PipelinesPage } from "./pages/PipelinesPage";
import { MomentumPage } from "./pages/MomentumPage";
import { FinancePage } from "./pages/FinancePage";
import { TwilioPage } from "./pages/TwilioPage";
import { ProspectsPage } from "./pages/ProspectsPage";
import { MarketGapsPage } from "./pages/MarketGapsPage";
import { ConnieDashboardPage } from "./pages/ConnieDashboardPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { ExecutiveSeoReportPage } from "./pages/ExecutiveSeoReportPage";
import { ContentPage } from "./pages/ContentPage";
import { getToken } from "./lib/auth";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  return getToken() ? <>{children}</> : <Navigate to="/login" replace />;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<RequireAuth><Layout /></RequireAuth>}>
            <Route index element={<CommandCenterPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/sites" element={<SitesPage />} />
            <Route path="/sites/:id" element={<SiteDetailPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/journo" element={<JournoPage />} />
            <Route path="/outreach" element={<OutreachPage />} />
            <Route path="/citations" element={<CitationsPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/ops" element={<OpsPage />} />
            <Route path="/ops/finance" element={<FinancePage />} />
            <Route path="/ops/twilio" element={<TwilioPage />} />
            <Route path="/ops/prospects" element={<ProspectsPage />} />
            <Route path="/markets/gaps" element={<MarketGapsPage />} />
            <Route path="/agents/connie" element={<ConnieDashboardPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/optimize" element={<OptimizePage />} />
            <Route path="/optimize/impact" element={<OptimizeImpactPage />} />
            <Route path="/agents/pending" element={<AgentsPendingPage />} />
            <Route path="/agents/:id" element={<AgentDetailPage />} />
            <Route path="/pipelines" element={<PipelinesPage />} />
            <Route path="/momentum" element={<MomentumPage />} />
            <Route path="/content" element={<ContentPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/reports/executive-seo" element={<ExecutiveSeoReportPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
